import { CheckCircle, X, TrendingUp, Zap, Clock, AlertTriangle, DollarSign } from "lucide-react";

export function PriceComparisonSection({ t }) {
  return (
    <section className="py-24 px-6 bg-[var(--color-bg)] border-t border-[var(--color-border)] overflow-hidden" data-testid="price-comparison-section">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <p className="text-[#a855f7] font-['JetBrains_Mono'] text-sm tracking-[0.3em] uppercase mb-4" data-testid="price-compare-tag">
            {t.pc_tag || "Stop overpaying for SEO"}
          </p>
          <h2 className="text-4xl sm:text-5xl font-black font-['Cabinet_Grotesk'] tracking-tighter mb-4">
            {t.pc_h1 || "Replace expensive SEO agencies"}<br />
            <span className="text-[#a855f7]">{t.pc_h1_highlight || "for the price of a family dinner"}</span>
          </h2>
          <p className="text-[var(--color-text-muted)] text-lg max-w-2xl mx-auto">
            {t.pc_sub || "You can try it for free and start ranking today"}
          </p>
        </div>

        {/* 3-column comparison */}
        <div className="grid md:grid-cols-3 gap-0 max-w-5xl mx-auto mt-12" data-testid="price-compare-grid">
          {/* Google Ads */}
          <div className="bg-[var(--color-surface)] border border-[var(--color-border)] p-6 md:p-8" data-testid="price-col-ads">
            <div className="text-center mb-6">
              <p className="text-[var(--color-text-muted)] font-['JetBrains_Mono'] text-xs uppercase tracking-wider mb-2">Google Ads</p>
              <p className="text-4xl sm:text-5xl font-black font-['Cabinet_Grotesk'] text-[var(--color-text)]">
                €3 200
              </p>
              <p className="text-[var(--color-text-muted)] text-sm font-['JetBrains_Mono']">/{t.pc_month || "month"}</p>
            </div>
            <div className="space-y-3">
              {[
                t.pc_ads_1 || "You need a big budget",
                t.pc_ads_2 || "Manual keyword research",
                t.pc_ads_3 || "Optimized for clicks, not sales",
                t.pc_ads_4 || "Rented traffic",
                t.pc_ads_5 || "Competitors outbid you",
                t.pc_ads_6 || "Traffic stops when you stop spending",
                t.pc_ads_7 || "€0 in lasting results",
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <X className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-[var(--color-text-muted)]">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* TopRankPulse — highlighted center */}
          <div className="bg-[#a855f7] p-6 md:p-8 relative z-10 md:scale-[1.05] md:-my-4 shadow-2xl shadow-purple-900/30" data-testid="price-col-trp">
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-[#FFE600] text-black text-xs font-black uppercase tracking-wider whitespace-nowrap">
              {t.pc_best_value || "BEST VALUE"}
            </div>
            <div className="text-center mb-6">
              <div className="flex items-center justify-center gap-2 mb-2">
                <div className="w-6 h-6 bg-white flex items-center justify-center"><TrendingUp className="w-4 h-4 text-[#a855f7]" /></div>
                <span className="text-white font-black font-['Cabinet_Grotesk'] text-sm">TopRankPulse</span>
              </div>
              <p className="text-5xl sm:text-6xl font-black font-['Cabinet_Grotesk'] text-white">
                €49<span className="text-2xl">.00</span>
              </p>
              <p className="text-white/70 text-sm font-['JetBrains_Mono']">/{t.pc_month || "month"}</p>
            </div>
            <div className="space-y-3">
              {[
                t.pc_trp_1 || "AI writes quality daily content",
                t.pc_trp_2 || "Automatic keyword research",
                t.pc_trp_3 || "Quality backlinks included",
                t.pc_trp_4 || "No contracts — cancel anytime",
                t.pc_trp_5 || "Rank once, get traffic forever",
                t.pc_trp_6 || "No technical knowledge needed",
                t.pc_trp_7 || "Built to bring buyers, not random traffic",
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <CheckCircle className="w-4 h-4 text-white flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-white font-medium">{item}</span>
                </div>
              ))}
            </div>
            <a href="#pricing" className="block mt-6 w-full py-3 bg-white text-[#a855f7] text-center font-black uppercase tracking-wider hover:bg-zinc-100 transition-colors text-sm" data-testid="price-compare-cta">
              {t.pc_cta || "START FREE TRIAL"}
            </a>
          </div>

          {/* SEO Agency */}
          <div className="bg-[var(--color-surface)] border border-[var(--color-border)] p-6 md:p-8" data-testid="price-col-agency">
            <div className="text-center mb-6">
              <p className="text-[var(--color-text-muted)] font-['JetBrains_Mono'] text-xs uppercase tracking-wider mb-2">{t.pc_agency || "SEO Agency"}</p>
              <p className="text-4xl sm:text-5xl font-black font-['Cabinet_Grotesk'] text-[var(--color-text)]">
                €2 300
              </p>
              <p className="text-[var(--color-text-muted)] text-sm font-['JetBrains_Mono']">/{t.pc_month || "month"}</p>
            </div>
            <div className="space-y-3">
              {[
                t.pc_ag_1 || "Months to see results",
                t.pc_ag_2 || "Weekly meetings",
                t.pc_ag_3 || "Locked into contracts",
                t.pc_ag_4 || "2 blog posts/month for €2,300",
                t.pc_ag_5 || "They own everything",
                t.pc_ag_6 || "Traffic up 200%, Sales 0%",
                t.pc_ag_7 || "No AI visibility optimization",
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <X className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-[var(--color-text-muted)]">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Savings banner */}
        <div className="max-w-5xl mx-auto mt-12 bg-[var(--color-surface)] border border-[var(--color-border)] p-6 md:p-8" data-testid="savings-banner">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-emerald-900/30 flex items-center justify-center flex-shrink-0">
                <DollarSign className="w-7 h-7 text-emerald-400" />
              </div>
              <div>
                <p className="text-2xl sm:text-3xl font-black font-['Cabinet_Grotesk'] text-white">
                  {t.pc_save_title || "Save 20+ hours & €2,000+/month"}
                </p>
                <p className="text-[var(--color-text-muted)] text-sm mt-1">
                  {t.pc_save_sub || "Switch from agencies or Google Ads to TopRankPulse and keep more money in your pocket."}
                </p>
              </div>
            </div>
            <a href="#pricing" className="flex-shrink-0 px-8 py-3 bg-emerald-500 text-white font-black uppercase tracking-wider hover:bg-emerald-400 transition-colors text-sm" data-testid="savings-cta">
              {t.pc_save_cta || "SEE PLANS"}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
